import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Calendar, AlertTriangle } from 'lucide-react';
import { getHolidayNotifications, dismissHoliday, dismissAllHolidays } from '@/services/bookingApi';
import type { HolidayNotification } from '@/types';

interface HolidayNotificationBannerProps {
  /** Optional: refresh interval in ms (default 0 = no refresh) */
  refreshInterval?: number;
}

export const HolidayNotificationBanner: React.FC<HolidayNotificationBannerProps> = ({
  refreshInterval = 0,
}) => {
  const [notification, setNotification] = useState<HolidayNotification | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDismissed, setIsDismissed] = useState(false);
  const { t } = useTranslation('dashboard');

  useEffect(() => {
    fetchNotification();

    if (refreshInterval > 0) {
      const interval = setInterval(fetchNotification, refreshInterval);
      return () => clearInterval(interval);
    }
  }, [refreshInterval]);

  const fetchNotification = async () => {
    try {
      const data = await getHolidayNotifications();
      setNotification(data);
      setIsDismissed(false);
    } catch (err) {
      console.error('Failed to fetch holiday notifications:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDismissAll = async () => {
    try {
      await dismissAllHolidays();
      setIsDismissed(true);
    } catch (err) {
      console.error('Failed to dismiss holidays:', err);
    }
  };

  const handleDismissOne = async (holidayId: string) => {
    try {
      await dismissHoliday(holidayId);
      // Update local state to remove the dismissed holiday
      if (notification) {
        const updated = {
          ...notification,
          holidays: notification.holidays.filter(h => h.id !== holidayId),
        };
        if (updated.holidays.length === 0) {
          setIsDismissed(true);
        } else {
          setNotification(updated);
        }
      }
    } catch (err) {
      console.error('Failed to dismiss holiday:', err);
    }
  };

  // Don't render if loading, dismissed, or no notifications
  if (isLoading || isDismissed || !notification?.has_notifications) {
    return null;
  }

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
      <div className="flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
        <div className="flex-1">
          <h4 className="font-medium text-amber-900">{t('holidays.notificationTitle')}</h4>
          <p className="text-sm text-amber-700 mt-1">{notification.message}</p>

          <div className="mt-3 space-y-2">
            {notification.holidays.map((holiday) => (
              <div key={holiday.id} className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2 text-amber-800">
                  <Calendar className="w-4 h-4" />
                  <span>{holiday.name}</span>
                  <span className="text-amber-600">({holiday.date})</span>
                  <span className="text-amber-500">- {holiday.days_away} days</span>
                </div>
                <button
                  onClick={() => handleDismissOne(holiday.id)}
                  className="text-amber-400 hover:text-amber-600"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>

          <button
            onClick={handleDismissAll}
            className="mt-3 text-sm text-amber-700 hover:text-amber-900 underline"
          >
            {t('holidays.dismissAll')}
          </button>
        </div>
      </div>
    </div>
  );
};
